import { captureRemixErrorBoundaryError } from "@sentry/remix";
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useMatches,
  useRouteError,
} from "@remix-run/react";
import { createCookie, LinksFunction, LoaderFunction } from "@remix-run/node";

import "./tailwind.css";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import { CartContextProvider } from "./context/CartContext";
import { createCart, getCart } from "./shopify/cart";
import { getSession } from "./auth/session";
import { adminAuth } from "./firebase.server";

export const links: LinksFunction = () => [
  { rel: "icon", href: "/favicon.png" },
];

interface LoaderData {
  cart: ReturnType<typeof getCart>;
  isAdmin: boolean;
}

export const loader: LoaderFunction = async ({ request }) => {
  let isAdmin = false;
  const headers = new Headers();

  const cookie = request.headers.get("Cookie");
  const session = await getSession(cookie);
  const sessionCookie = session.get("sessionCookie");

  // CHECK LOGIN STATUS
  if (sessionCookie) {
    try {
      // Verify the session cookie. You can also check for additional claims if needed.
      const decodedClaims = await adminAuth.verifySessionCookie(
        sessionCookie,
        true
      );
      isAdmin = decodedClaims?.email !== null;
    } catch (e) {
      console.error(e);
    }
  }

  // CHECK AND SET REFERRAL COOKIE
  const searchParams = new URL(request.url).searchParams;
  const referral = searchParams.get("ref");
  if (referral) {
    const referralCookie = createCookie("ref", {
      maxAge: 60 * 60 * 24 * 30, // 30 days
    });
    headers.append("Set-Cookie", await referralCookie.serialize(referral));
  }

  // SET CART COOKIE
  const cartCookie = createCookie("cart", {
    maxAge: 60 * 60 * 24 * 7, // 1 week
  });

  const cartData = await cartCookie.parse(request.headers.get("Cookie"));
  if (!cartData) {
    const newCart = await createCart();
    headers.append("Set-Cookie", await cartCookie.serialize(newCart));
  }

  return Response.json(
    { cart: await getCart(cartData?.id), isAdmin },
    { headers }
  );
};

const FrontendLayout = ({ children }: { children: React.ReactNode }) => {
  const loaderData = useLoaderData<LoaderData>();
  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
        <script
          defer
          src="https://cloud.umami.is/script.js"
          data-website-id="e7b03ef1-5d1b-4bf6-b1aa-2fa7b37eb5fe"
        ></script>
      </head>
      <body>
        <CartContextProvider cartData={loaderData?.cart}>
          <Navbar isAdmin={loaderData?.isAdmin} />
          {children}
          <Footer />
          <ScrollRestoration />
          <Scripts />
        </CartContextProvider>
      </body>
    </html>
  );
};

const AdminLayout = ({ children }: { children: React.ReactNode }) => {
  return (
    <html lang="en" className="h-full bg-gray-100">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
      </head>
      <body className="bg-gray-100 min-h-full">
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
};

export function Layout({ children }: { children: React.ReactNode }) {
  const matches = useMatches();
  // Check if one of the matches is an admin route.
  // This assumes your admin routes have an id that starts with "routes/admin"
  const isAdminRoute = matches.some((match) =>
    match.id?.startsWith("routes/admin")
  );

  return isAdminRoute ? (
    <AdminLayout>{children}</AdminLayout>
  ) : (
    <FrontendLayout>{children}</FrontendLayout>
  );
}

export const ErrorBoundary = () => {
  const error = useRouteError();
  captureRemixErrorBoundaryError(error);
  return <div>Something went wrong</div>;
};

export default function App() {
  return <Outlet />;
}